/**
 * What making a record public actually does, one sentence per record type
 * and state (DESIGN_SYSTEM.md §5 "Copy tells the truth").
 *
 * These sentences describe live cross-organization behaviour, so they are
 * load-bearing. Update them whenever the API's matching or composition rules
 * change:
 * - sensor: api/src/services/sensors.ts `listSensorsInRegionForSubscriber`
 *   (own organization OR is_public), called by the subscription worker in
 *   api/src/services/subscriptionMatching.ts.
 * - lens: the lens gate in api/src/services/subscriptionMatching.ts
 *   (`matchSubscription`: own organization OR lens.isPublic).
 * - template: attachable = own OR public, and a public lens cannot hold a
 *   private template (api/src/services/lenses.ts).
 *
 * Every sentence must stay true on member AND System Admin surfaces, so none
 * says "your organization" (an administrator edits other organizations'
 * records). No "CRUD", no "org", no permission keys.
 */
export type VisibilityKind = 'sensor' | 'lens' | 'template';

export interface VisibilityCopy {
  /** The fixed visible label. It never changes with the value. */
  label: string;
  /** The consequence while public. */
  public: string;
  /** The consequence while private. */
  private: string;
}

export const VISIBILITY_COPY: Record<VisibilityKind, VisibilityCopy> = {
  sensor: {
    label: 'Share with other organizations',
    public: "Other organizations' subscriptions that cover this sensor's area can receive its events.",
    private: "Only its own organization's subscriptions receive this sensor's events.",
  },
  lens: {
    label: 'Public lens',
    public:
      'App clients in any organization can subscribe through this lens; only the organization that owns it can edit it.',
    private: 'Only app clients in the organization that owns this lens can subscribe through it.',
  },
  template: {
    label: 'Available to other organizations',
    public: 'Any organization can add this template to its lenses.',
    private:
      'Only the organization that owns this template can add it, and only to lenses that are not public.',
  },
};

/** The consequence sentence for a record type in a given state. */
export function visibilityConsequence(kind: VisibilityKind, isPublic: boolean): string {
  return isPublic ? VISIBILITY_COPY[kind].public : VISIBILITY_COPY[kind].private;
}
